import { Box, MenuItem, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CandidateRejectionDetails,
  CandidateRejectionReason,
  CANDIDATE_REJECTION_REASONS,
} from "../../../types/CandidateRejectionReason";
import BasicDialog from "../dialogs/BasicDialog";
import InfoTooltip from "../InfoTooltip/InfoTooltip";
import { useCandidateRejectionReasons } from "./useCandidateRejectionReasons";

type Props = {
  open: boolean;
  talentName: string;
  candidateCount?: number;
  title?: string;
  onClose: () => void;
  onReject: (details: CandidateRejectionDetails) => Promise<boolean>;
};

type Values = { reason: CandidateRejectionReason | ""; comment: string };

export const CandidateRejectionDialog = ({
  open,
  talentName,
  candidateCount = 1,
  title,
  onClose,
  onReject,
}: Props) => {
  const { t } = useTranslation();
  const labels = useCandidateRejectionReasons();
  const {
    control,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<Values>({ defaultValues: { reason: "", comment: "" } });
  const reason = watch("reason");
  const showComment = candidateCount === 1;
  useEffect(() => {
    if (open) reset({ reason: "", comment: "" });
  }, [open, talentName, candidateCount, reset]);

  const submit = handleSubmit(async ({ reason, comment }) => {
    const details: CandidateRejectionDetails = reason
      ? {
          rejectionReason: reason,
          ...(showComment && reason === "OtherReason" && comment.trim()
            ? { rejectionComment: comment }
            : {}),
        }
      : {};
    if (await onReject(details)) onClose();
  });

  return (
    <BasicDialog
      open={open}
      title={title ?? t("order:notSuitable")}
      onCancel={isSubmitting ? undefined : onClose}
      onConfirm={submit}
      confirmLabel={t("common:confirm")}
      confirmDisabled={isSubmitting}
      PaperProps={{ sx: { minWidth: 480 } }}
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ whiteSpace: "pre-line" }}>{talentName}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  disabled={isSubmitting}
                  label={t("common:rejectionReasonOptional")}
                  onChange={(event) => {
                    field.onChange(event);
                    setValue("comment", "");
                  }}
                >
                  <MenuItem value="">{t("common:noSelection")}</MenuItem>
                  {CANDIDATE_REJECTION_REASONS.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {labels[reason]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {candidateCount > 1 && (
              <InfoTooltip
                title={t("common:rejectionReasonAppliesToAllSelectedTalents")}
                icon={
                  <InfoOutlinedIcon
                    tabIndex={0}
                    aria-label={t(
                      "common:rejectionReasonAppliesToAllSelectedTalents",
                    )}
                  />
                }
              />
            )}
          </Box>
          {showComment && reason === "OtherReason" && (
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={isSubmitting}
                  label={t("common:rejectionCommentOptional")}
                  inputProps={{ maxLength: 2000 }}
                />
              )}
            />
          )}
        </Box>
      }
    />
  );
};
